/**
 * optionGroupDraft.js
 *
 * The rules a "Create New Option Group" form has to hold before it posts.
 *
 * The modal used to enforce almost nothing: `required` on the name and on two
 * number fields that both defaulted to 0. A maximum of 0 passes `required`,
 * because 0 is a value, and it produces a group the customer can choose
 * nothing from. The options list was not checked at all, so an empty group
 * could be created and attached to a dish.
 *
 * Kept as a pure function so the same rules can be applied by the other three
 * product forms that carry a copy of this modal.
 */

const count = (n) => (Number.isFinite(Number(n)) ? Number(n) : NaN);

/**
 * Returns an empty string when the draft is good to post, otherwise the one
 * message to show the operator. Order matters: report the first thing that is
 * wrong going down the form, so the message always points at the field nearest
 * the top that needs attention.
 */
export const validateOptionGroupDraft = ({
  name = "",
  selectedCount = 0,
  minOptions = 0,
  maxOptions = 0,
} = {}) => {
  const trimmed = String(name || "").trim();
  const min = count(minOptions);
  const max = count(maxOptions);
  const options = count(selectedCount);

  if (!trimmed) return "Give the group a name, for example Offals or Beef.";
  if (!options) {
    return "Add at least one option. Use the row below to create one if this kitchen has none yet.";
  }
  if (!Number.isFinite(min) || min < 0) return "Minimum cannot be negative.";
  if (!Number.isFinite(max) || max < 1) {
    return "Maximum must be at least 1, otherwise nothing can be chosen.";
  }
  if (max < min) return "Maximum cannot be lower than the minimum.";
  if (min > options || max > options) {
    return `This group has ${options} option${options === 1 ? "" : "s"}, so the minimum and maximum cannot be higher than ${options}.`;
  }
  return "";
};

/** The rules for one option (a single choice such as "Offals"). */
export const validateOptionDraft = ({ name = "", price = "", existingNames = [] } = {}) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "Give the option a name, for example Offals.";

  const amount = price === "" || price === null || price === undefined ? 0 : Number(price);
  if (!Number.isFinite(amount) || amount < 0) {
    return "An option's extra charge cannot be negative.";
  }
  const clash = (existingNames || []).some(
    (existing) => String(existing || "").trim().toLowerCase() === trimmed.toLowerCase(),
  );
  if (clash) return `"${trimmed}" already exists. Pick it from the list above.`;
  return "";
};

export default { validateOptionGroupDraft, validateOptionDraft };
